// 콤보+직접 입력 하이브리드 — 식별 키(BindingKeyPicker)와 표시 형식의 경로 칸(병기 값·구간 키)이
// 공유하는 단일 메커니즘. 어휘(문구)만 호출부가 정하고 동작 정본은 여기 하나다.
// 결정 배경: 경로를 외워 타이핑하는 칸은 오타가 "아무것도 안 나옴"으로만 드러나 저작자가 원인을
// 알 수 없다 — 후보를 보여 주되 **후보를 강제하지는 않는다**(선언이 불완전할 수 있다).

import { useState } from 'react';
import { INPUT_CLS, SELECT_CLS } from './editorFieldClasses';
import type { RepeatSourceField } from './repeatSources';
import { rowScopeChildOptions } from './rowScopeKeys';

/** 콤보 후보 1건 — key=저장되는 값, label=목록에 보이는 문구(키 병기는 라벨을 만드는 쪽 몫). */
export interface HybridKeyOption {
  key: string;
  label: string;
}

/** 직접 입력 모드로 전환하는 특수 옵션 값 — 실제 키와 충돌하지 않도록 이중 밑줄 관례. */
const DIRECT_OPTION = '__direct__';

/**
 * 행 필드 → 콤보 후보. 라벨에 키를 병기한다(라벨만으론 저장값을 알 수 없다). 후보가 없으면 빈
 * 배열(소비 측이 length로 "후보 없음 → 자유 텍스트 입력" degrade를 판단한다).
 *
 * 중첩(객체/배열) 필드 **자체**는 여전히 제외한다(표시할 스칼라가 아니다). 다만 그 필드에
 * widgetKey 계약이 있거나 카탈로그가 자식을 선언했으면 **자식 경로는 스칼라**이므로
 * `위험도 › 빈도 (가능성)`·`위험요인 유형(13칸) › 유형 ✓ — 추락` 형태로 합류한다
 * (`rowScopeChildOptions` — 계약 ∪ 선언) — bare 바인딩 읽기가 `getByPath`라 점 경로가 실제로
 * 성립하는데도(엔진은 읽는데) 이 목록이 자식을 감추고 있었던 탓에, 저작자가 경로를 외워
 * 타이핑해야만 닿을 수 있었다.
 * 계약도 선언도 없는 중첩 필드는 종전대로 직접 입력 몫이다(자식이 무엇인지 선언된 바가 없다).
 */
export function rowFieldPickerOptions(
  fields: RepeatSourceField[] | undefined,
): HybridKeyOption[] {
  const out: HybridKeyOption[] = [];
  for (const f of fields ?? []) {
    if (!f.nested) out.push({ key: f.key, label: f.label ? `${f.label} (${f.key})` : f.key });
    for (const child of rowScopeChildOptions(f)) out.push(child);
  }
  return out;
}

/**
 * 콤보+직접 입력 하이브리드 본체.
 *
 * **후보 밖 값은 감추지 않는다.** 현재 값이 후보에 없으면 직접 입력 모드로 시작하고(값이 그대로
 * 보인다), 목록으로 돌아가도 `(후보 밖)` 옵션으로 남긴다 — 콤보가 빈 칸으로 보이면 기존 저작이
 * 사라진 것처럼 읽히고, 무심코 다른 후보를 고르면 덮어쓰기 사고가 된다.
 */
export function HybridKeyPicker({
  value,
  options,
  onChange,
  placeholder,
  chooseLabel,
  emptyLabel,
  invalid,
  invalidMessage,
  hint,
  onFocus,
  onBlur,
}: {
  value: string;
  options: HybridKeyOption[];
  onChange: (key: string) => void;
  /** 직접 입력 모드 placeholder. */
  placeholder: string;
  /** 값 미선택 상태에서 콤보 첫 줄에 보일 문구. */
  chooseLabel: string;
  /** 지정하면 "지정 안 함"을 고를 수 있다(빈 문자열로 onChange). 미지정이면 비우는 선택지 없음. */
  emptyLabel?: string;
  /** 직접 입력 값이 경고 상태(식별 키 중복 등)면 true — 직접 입력 모드에서만 표시. */
  invalid?: boolean;
  invalidMessage?: string;
  /** 콤보 아래 보조 설명. */
  hint?: string;
  /**
   * 값 칸(콤보·직접 입력 양쪽)의 포커스 진입/이탈. 식별 키로 쓰일 때 "같은 키를 쓰는 다른 자리"
   * 캔버스 강조를 켜고 끈다(sharedKeyHighlight). 표시 형식의 경로 칸처럼 캔버스에 대응 자리가
   * 없는 소비처는 넘기지 않는다.
   */
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  const known = options.some((o) => o.key === value);
  // ⚠️ 마운트 시점 초기값 — 편집 대상이 바뀌어도 따라가지 않으므로, 대상 식별자를 아는 사용처는
  // key로 리마운트하는 편이 낫다(BindingEditor가 key={node.id} 부여). 다만 **어느 모드에서도 현재
  // 값은 그대로 보이므로**(목록 모드는 "(후보 밖)" 항목으로 보존) 리마운트 누락은 "콤보 대신
  // 텍스트 칸이 떠 있다" 수준의 표시 문제이지 덮어쓰기 사고가 아니다.
  const [direct, setDirect] = useState(value !== '' && !known);

  if (direct) {
    return (
      <div className="space-y-0.5">
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={
              invalid
                ? `${INPUT_CLS} font-mono border-red-400 focus:ring-red-400`
                : `${INPUT_CLS} font-mono`
            }
            placeholder={placeholder}
            aria-invalid={invalid || undefined}
            onFocus={onFocus}
            onBlur={onBlur}
          />
          <button
            type="button"
            className="shrink-0 text-[11px] text-gray-500 underline"
            onClick={() => setDirect(false)}
            title="후보 목록에서 선택"
          >
            목록
          </button>
        </div>
        {invalid && invalidMessage && (
          <p className="text-[11px] text-red-600 leading-snug">{invalidMessage}</p>
        )}
      </div>
    );
  }

  // 후보 밖 현재값 — 목록 모드에서도 선택된 채로 남긴다(조용히 비우지 않는다).
  const preserved = value !== '' && !known;
  return (
    <div className="space-y-1">
      <select
        value={known || preserved ? value : ''}
        onChange={(e) => {
          if (e.target.value === DIRECT_OPTION) {
            setDirect(true);
            return;
          }
          // 빈 값 선택은 "지정 안 함"이 있을 때만 유효(없으면 안내용 첫 줄이라 무시).
          if (e.target.value || emptyLabel !== undefined) onChange(e.target.value);
        }}
        className={SELECT_CLS}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {emptyLabel !== undefined ? (
          <option value="">{emptyLabel}</option>
        ) : !known && !preserved ? (
          <option value="">{chooseLabel}</option>
        ) : null}
        {preserved && <option value={value}>{value} (후보 밖)</option>}
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
        <option value={DIRECT_OPTION}>직접 입력…</option>
      </select>
      {hint && <p className="text-[11px] text-gray-400 leading-snug">{hint}</p>}
    </div>
  );
}
